import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Grid } from "@material-ui/core";
import moment from "moment";

import {
  selectExecutionBriefId,
  selectExecutionResourceType,
} from "../../../../redux/selectors/executionBriefs";
import {
  selectWorkOrdersLoader,
  selectWorkOrdersResults,
} from "../../../../redux/selectors/workOrders";
import {
  getWorkOrderListInExecutionBrief,
  saveWorkOrder,
} from "../../../../redux/actions/workOrderActions";
import AddIcon from "../../../../components/icon/icons/layer-22-icon.component";
import usePermissions from "../../../../hooks/usePermissions";
import { BasicResourceType } from "../../../../types/resources/resources.enums";
import { BorderedBlock } from "../../../../features/common-attributes/components/bordered-block/bordered-block.component";
import { EXECUTION_TEMPLATE } from "../../../../types/templates/execution-brief/execution-brief-template.enum";
import { ListingTable } from "../../../../components/layout/listing-table/listing-table.component";
import { OutlinedButton } from "../../../../components/form-controls/button/outlined-button/outlined-button.component";
import { SpinnerLoader } from "../../../../components/loaders";
import { TRow } from "../../../../components/layout/listing-table/listing-table.types";
import { TWorkOrderResults } from "../../../../redux/reducers/workOrdersReducer";
import { WORK_ORDER_TEMPLATE } from "../../../../types/templates/work-order/work-order-template.enum";
import { WorkOrderProps } from "../../../../features/brief-list/types/props/work-order.props";
import { getResourceLink } from "../../../../features/navigation/types/pages.functions";

import { workOrderListingColumns } from "./execution-brief-work-order-tab.configuration";
import { HiddenHeading } from "../../../pages.styles";

export interface ExecutionBriefWorkOrderTabProps {
  pageTitle?: string;
}

export const ExecutionBriefWorkOrderTab = (props: ExecutionBriefWorkOrderTabProps) => {
  const history = useHistory();
  const userPermissions = usePermissions();
  const dispatch = useDispatch();
  const isLoading = useSelector(selectWorkOrdersLoader);

  const executionBriefId = useSelector(selectExecutionBriefId);

  useEffect(() => {
    if (executionBriefId) {
      dispatch(getWorkOrderListInExecutionBrief(executionBriefId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionBriefId]);

  const workOrderListResult = useSelector(selectWorkOrdersResults);

  const convertData = (origData?: TWorkOrderResults[]): WorkOrderProps[] => {
    if (origData) {
      return origData.map((item) => ({
        ...item,
        createdDate: moment(item.createdAt).format("YYYY-MM-DD"),
        dueDate: item.dueAt ? moment(item.dueAt).format("MMM DD") : "-",
      }));
    } else {
      return [];
    }
  };

  const sortedDataList = convertData(workOrderListResult).sort((a, b) =>
    a.createdAt > b.createdAt ? -1 : 1
  ); // Sorted by created datetime descending

  let resourceType = "";
  const executionBriefTempalte = useSelector(selectExecutionResourceType);

  switch (executionBriefTempalte) {
    case EXECUTION_TEMPLATE.SIMPLE:
      resourceType = WORK_ORDER_TEMPLATE.SIMPLE;
      break;
    case EXECUTION_TEMPLATE.WEB:
      resourceType = WORK_ORDER_TEMPLATE.WEB;
      break;
    case EXECUTION_TEMPLATE.DEALER_ADVERTISING:
      resourceType = WORK_ORDER_TEMPLATE.DEALER_ADVERTISING;
      break;
    case EXECUTION_TEMPLATE.EDM:
      resourceType = WORK_ORDER_TEMPLATE.EDM;
      break;
    case EXECUTION_TEMPLATE.INSTORE_PRINT:
      resourceType = WORK_ORDER_TEMPLATE.INSTORE_PRINT;
      break;
    case EXECUTION_TEMPLATE.INSTORE_DIGITAL:
      resourceType = WORK_ORDER_TEMPLATE.INSTORE_DIGITAL;
      break;
    case EXECUTION_TEMPLATE.PAID_MEDIA:
      resourceType = WORK_ORDER_TEMPLATE.PAID_MEDIA;
      break;
    default:
      resourceType = "";
  }

  const handleNewWorkOrder = () => {
    dispatch(saveWorkOrder({ executionBriefId, resourceType }, history));
  };

  const getLink = (workOrder: TRow) =>
    getResourceLink(workOrder.id, BasicResourceType.WorkOrder) ?? "";

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HiddenHeading>{props.pageTitle}</HiddenHeading>
          <BorderedBlock>
            <ListingTable
              columns={workOrderListingColumns}
              dataList={sortedDataList}
              link={getLink}
            />
            <OutlinedButton
              disabled={!userPermissions.manageWorkOrders}
              onClick={handleNewWorkOrder}
              variant={"outlined"}
              startIcon={<AddIcon />}
            >
              Add Brief Work Order
            </OutlinedButton>
          </BorderedBlock>
          <SpinnerLoader showIf={isLoading.status === "loading"} innerLoader={true} />
        </Grid>
      </Grid>
    </>
  );
};
